import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

type SessionContextValue = {
  signedIn: boolean;
  onboarded: boolean;
  signIn: () => void;
  signOut: () => void;
  finishOnboarding: () => void;
};

const SessionContext = createContext<SessionContextValue | null>(null);

const readFlag = (key: string, fallback: boolean) => {
  try {
    const value = window.localStorage.getItem(key);
    return value === null ? fallback : value === 'true';
  } catch {
    return fallback;
  }
};

export function SessionProvider({ children }: { children: ReactNode }) {
  const [signedIn, setSignedIn] = useState(() => readFlag('mgenie-demo-session', false));
  const [onboarded, setOnboarded] = useState(() => readFlag('mgenie-demo-onboarded', true));

  const value = useMemo<SessionContextValue>(() => ({
    signedIn,
    onboarded,
    signIn: () => {
      setSignedIn(true);
      window.localStorage.setItem('mgenie-demo-session', 'true');
    },
    signOut: () => {
      setSignedIn(false);
      window.localStorage.setItem('mgenie-demo-session', 'false');
    },
    finishOnboarding: () => {
      setOnboarded(true);
      window.localStorage.setItem('mgenie-demo-onboarded', 'true');
    },
  }), [onboarded, signedIn]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession must be used inside SessionProvider');
  return session;
}
